<template>
  <v-row no-gutters class="fill-height flex-column" style="overflow-y: auto; overflow-x: hidden;">
    <v-col cols="12" class="pr-6" v-if="fetched">
      <card-title-row :straightTitle="additionalModuleName" class="pt-3 pb-5" />
      <v-row no-gutters>
        <v-col cols="12">
          <stepper
            ref="stepper"
            :steps="steps"
            :stepper-props="{ altLabels: true }"
            :stepper-classes="['elevation-0']"
            :stepper-header-classes="['elevation-0', 'pl-5']"
            :stepper-buttons-classes="['pl-8']"
          >
            <template #1>
              <sidebar-row>
                <v-col cols="12">
                  <v-row no-gutters>
                    <v-radio-group class="mt-0 pt-0" hide-details dense row v-model="pointType">
                      <v-col cols="auto" class="pa-0 pr-1">
                        <v-radio :label="$i18n.t('sidebar.locationAnalysis.pointOnMap')" value="map"></v-radio>
                      </v-col>
                      <v-col cols="auto" class="pa-0">
                        <v-radio :label="$i18n.t('sidebar.locationAnalysis.featureOnLayer')" value="layer"></v-radio>
                      </v-col>
                    </v-radio-group>
                  </v-row>
                  <v-row no-gutters class="pt-4">
                    <v-col cols="12">
                      <template v-if="pointType === 'map'">
                        <v-row no-gutters>
                          <data-input ref="geometry-input" v-bind="pointGeomInput" v-model="geometry" />
                        </v-row>
                        <v-row v-if="params['start_point']" no-gutters class="pa-2 mt-4 border text-body-2">
                          <v-col cols="auto">
                            {{ currentEpsgCoordinates?.join(', ') }}
                          </v-col>
                          <v-spacer />
                          <v-col cols="auto">
                            <v-icon size="20" @click="zoomToCoordinates">mdi-magnify</v-icon>
                          </v-col>
                          <v-col cols="auto" class="pl-1">
                            <v-icon size="20" color="error" @click="deleteCoordinates">mdi-close</v-icon>
                          </v-col>
                        </v-row>
                      </template>
                      <template v-else-if="pointType === 'layer'">
                        <v-row no-gutters>
                          <v-col cols="9">
                            <data-input
                              v-bind="layerInput"
                              v-model="params['start_layer_id']"
                              @change="onLayerChanged"
                            />
                          </v-col>
                        </v-row>
                        <v-row no-gutters class="pt-3" align="center">
                          <v-col cols="9">
                            <data-input v-bind="featureInput" v-model="params['start_feature_id']" />
                          </v-col>
                          <v-col cols="3">
                            <data-input
                              ref="feature-input"
                              v-bind="featureMapInput"
                              @pushFeatures="onFeatureMapInput"
                            />
                          </v-col>
                        </v-row>
                      </template>
                    </v-col>
                  </v-row>
                </v-col>
              </sidebar-row>
            </template>
            <template #2>
              <sidebar-row>
                <v-col cols="12">
                  <v-row no-gutters>
                    <v-col cols="9">
                      <data-input v-bind="dataLayerInput" v-model="params['data_layer_id']" />
                    </v-col>
                  </v-row>
                  <v-row no-gutters class="pt-3">
                    <v-col cols="9">
                      <data-input v-bind="countColumnInput" v-model="params['data_count_column']" />
                    </v-col>
                  </v-row>
                  <v-row no-gutters class="pt-3">
                    <v-col cols="9">
                      <data-input v-bind="nameColumnInput" v-model="params['data_name_column']" />
                    </v-col>
                  </v-row>
                </v-col>
              </sidebar-row>
            </template>
            <template #3>
              <sidebar-row>
                <v-col cols="12">
                  <v-row no-gutters v-for="(transportType, idx) of transportTypes" :key="idx" class="pb-2">
                    <v-col cols="9">
                      <data-input v-bind="checkboxInput" v-model="params[transportType.name]">
                        <template #label>
                          <span class="text-body-2" :style="`color: ${$_colors.font}`">
                            <v-icon size="22" class="pr-1" color="rgba(0,0,0,0.7)"> {{ transportType.icon }} </v-icon>
                            {{ transportType.text }}
                          </span>
                        </template>
                      </data-input>
                    </v-col>
                  </v-row>
                </v-col>
              </sidebar-row>
            </template>
            <template #4>
              <sidebar-row>
                <v-col cols="12">
                  <v-row no-gutters>
                    <v-col cols="9">
                      <data-input v-bind="checkboxInput" v-model="saveResult">
                        <template #label>
                          <span class="text-body-2" :style="`color: ${$_colors.font}`">
                            {{ $i18n.t('default.saveAnalysisResult') }}
                          </span>
                        </template>
                      </data-input>
                    </v-col>
                  </v-row>
                  <v-row no-gutters v-if="saveResult" class="pt-2 pb-3">
                    <v-col cols="10">
                      <data-input v-bind="analysisNameInput" v-model="params['name']" />
                    </v-col>
                  </v-row>
                  <v-row no-gutters class="pt-2 pb-3" align="center">
                    <v-col cols="6">
                      <data-input v-bind="analysisTimeInput" v-model="params['time']" />
                    </v-col>
                    <v-col cols="auto" class="pl-3">
                      <base-button
                        class="text-body-2"
                        :translation-path="currentResult ? 'button.runAgain' : 'button.turnOnAnalysis'"
                        :disabled="!params['time'] || (saveResult && !params['name'])"
                        :loading="loading"
                        @click="runAnalysis()"
                      />
                    </v-col>
                  </v-row>
                  <v-row no-gutters v-if="currentResult" class="pt-2 pb-3" align="center">
                    <v-col cols="auto">
                      <base-button
                        class="text-body-2"
                        translation-path="sidebar.locationAnalysis.analysisResult"
                        icon="mdi-magnify"
                        :disabled="loading"
                        @click="openAnalysisResultDialog()"
                      />
                    </v-col>
                    <v-col cols="auto">
                      <base-button
                        class="text-body-2"
                        translation-path="button.reset"
                        cancel
                        :disabled="loading"
                        @click="resetAnalysis()"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </sidebar-row>
            </template>
          </stepper>
        </v-col>
      </v-row>
      <sidebar-row v-if="analyzes?.length">
        <v-row no-gutters class="pt-5">
          <v-col cols="12">
            <ribbon-list
              title-path="sidebar.locationAnalysis.resultAnalyzes"
              :list-items="analyzes"
              :toggle-max-items="5"
              item-title-key="name"
              @click="toggleAnalysisResult"
            >
              <template #itemButtons-append="{ item }">
                <dots-menu
                  :items="[{ name: 'changeName' }, { name: 'delete' }]"
                  @delete="onEditAnalysis(item, 'delete')"
                  @changeName="onEditAnalysis(item, 'changeName')"
                />
              </template>
            </ribbon-list>
          </v-col>
        </v-row>
      </sidebar-row>
      <main-dialog
        v-if="deleteDialogLoaded"
        :is-visible.sync="deleteDialogVisible"
        :title="$i18n.t('sidebar.locationAnalysis.deleteAnalysis')"
        :body="$i18n.t('sidebar.locationAnalysis.deleteAnalysisConfirmation', { name: currentEditingAnalysis?.name })"
        :loading="deleteDialogLoading"
        agree-text="dialog.agreeDelete"
        @agree="deleteAnalysisAction"
      />
      <main-input-dialog
        v-if="changeNameDialogLoaded"
        :is-visible.sync="changeNameDialogVisible"
        :loading="changeNameDialogLoading"
        :title-translation="$i18n.t('sidebar.locationAnalysis.changeName')"
        :inputs="editAnalysisInputs"
        agree-text="dialog.agreeSave"
        @agree="changeAnalysisNameAction"
      >
        <template #name.input="data">
          <v-col cols="6">
            <data-input v-model="currentEditingAnalysis[data.name]" v-bind="data" />
          </v-col>
        </template>
      </main-input-dialog>
      <location-analysis-result
        v-if="resultDialogLoaded"
        :is-visible.sync="resultDialogVisible"
        :loading.sync="resultDialogLoading"
        :analysis="currentAnalysis"
        :transport-types="transportTypes"
        @modalClosed="
          currentAnalysis = undefined;
          resultDialogLoaded = false;
        "
      />
    </v-col>
    <main-loader v-else />
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import MainLoader from '@/components/MainLoader';
import RibbonList from '@/components/RibbonList';
import SidebarRow from '@/components/SidebarRow';
import Stepper from '@/components/Stepper';

import { Point } from 'ol/geom';
import { transform } from 'ol/proj';

export default {
  name: 'LocationAnalysis',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    CardTitleRow,
    DataInput,
    DotsMenu,
    LocationAnalysisResult: () => import('@/components/LocationAnalysisResult'),
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainLoader,
    RibbonList,
    SidebarRow,
    Stepper,
  },
  computed: {
    additionalModuleName: get('admin/additionalModules@LOCATION_ANALYSIS_MODULE.verbose_name'),
    analyzes: get('locationAnalysis/analyzes'),
    cursorPositionEpsg: get('map/cursorPositionEpsg'),
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    layersArray() {
      return this.$_objectToArray(this.layers)
        .filter(layer => layer.geometry_type)
        .map(layer => {
          return { value: layer.id, text: layer.name, geometryType: layer.geometry_type };
        });
    },

    steps() {
      return [
        {
          step: 1,
          translationPath: 'sidebar.locationAnalysis.startPoint',
          nextDisabled: !this.firstStepValidated,
        },
        {
          step: 2,
          translationPath: 'sidebar.locationAnalysis.analysisData',
          nextDisabled: !this.secondStepValidated,
        },
        {
          step: 3,
          translationPath: 'sidebar.locationAnalysis.formsOfTransport',
          nextDisabled: !this.thirdStepValidated,
        },
        {
          step: 4,
          translationPath: 'sidebar.locationAnalysis.analysis',
          backDisabled: this.loading,
        },
      ];
    },
    firstStepValidated() {
      return this.pointType === 'map'
        ? !!this.params['start_point']
        : !!this.params['start_feature_id'] || this.params['start_feature_id'] === 0;
    },
    secondStepValidated() {
      return !!this.params['data_layer_id'] && !!this.params['data_name_column'] && !!this.params['data_count_column'];
    },
    thirdStepValidated() {
      return this.transportTypes.find(transportType => this.params[transportType.name]);
    },

    // Step 1
    // if pointType === 'map'
    layerInput() {
      return {
        dataType: { name: 'select' },
        readonly: this.layerFeaturesLoading,
        clearable: !this.layerFeaturesLoading,
        label: this.$i18n.t('default.layer'),
        hideDetails: true,
        items: this.layersArray,
      };
    },
    featureInput() {
      return {
        dataType: { name: 'select' },
        loading: this.layerFeaturesLoading,
        readonly: this.layerFeaturesLoading,
        clearable: true,
        label: this.$i18n.t('default.feature'),
        hideDetails: true,
        items: this.currentLayerFeatures,
        itemText: 'desc',
        itemValue: 'id',
      };
    },
    featureMapInput() {
      return {
        dataType: { name: 'features' },
        disabled: this.layerFeaturesLoading,
        simple: true,
        buttonProps: {
          isIcon: true,
          icon: 'mdi-cursor-pointer',
          iconSize: '20',
        },
        translationPath: '',
        featuresSettings: {
          layers: {
            included: this.params['start_layer_id'] ? [this.params['start_layer_id']] : [],
          },
        },
      };
    },
    // if pointType === 'layer'
    pointGeomInput() {
      return {
        dataType: { name: 'geometry' },
        name: 'locationAnalysisGeometry',
        buttonProps: {
          translationPath: 'sidebar.locationAnalysis.drawPoint',
        },
        disabled: this.params['start_point'] ? true : false,
      };
    },

    // Step 2
    dataLayerInput() {
      return {
        dataType: { name: 'select' },
        label: this.$i18n.t('sidebar.locationAnalysis.dataLayer'),
        hideDetails: true,
        items: this.layersArray.filter(layer => layer.geometryType === 'point'),
      };
    },
    currentDataLayerAttributes() {
      const layer = this.layers[this.params['data_layer_id']];
      return this.$_flattenStructure(
        this.getForm(
          layer?.form_schema?.elements || [],
          this.dataSources[layer?.data_source_name]?.attributes_schema?.attributes || []
        )
      ).map(attribute => {
        return {
          dataType: attribute.dataType,
          text: attribute.label,
          value: attribute.attribute,
        };
      });
    },
    currentDataLayerTextAttributes() {
      return this.currentDataLayerAttributes.filter(attr => attr.dataType.name === 'text');
    },
    currentDataLayerNumberAttributes() {
      return this.currentDataLayerAttributes.filter(attr =>
        ['float', 'decimal', 'integer'].includes(attr.dataType.name)
      );
    },
    nameColumnInput() {
      return {
        dataType: { name: 'select' },
        label: this.$i18n.t('sidebar.locationAnalysis.nameColumn'),
        hint: this.$i18n.t('sidebar.locationAnalysis.nameColumnSubtitle'),
        persistentHint: true,
        disabled: !this.params['data_layer_id'],
        items: this.currentDataLayerTextAttributes,
      };
    },
    countColumnInput() {
      return {
        dataType: { name: 'select' },
        label: this.$i18n.t('sidebar.locationAnalysis.countColumn'),
        hideDetails: true,
        disabled: !this.params['data_layer_id'],
        items: this.currentDataLayerNumberAttributes,
      };
    },

    // Step 3
    transportTypes() {
      return [
        { name: 'transit', icon: 'mdi-subway-variant', text: this.$i18n.t('sidebar.locationAnalysis.transit') },
        { name: 'driving', icon: 'mdi-car', text: this.$i18n.t('sidebar.locationAnalysis.driving') },
        { name: 'bicycling', icon: 'mdi-bike', text: this.$i18n.t('sidebar.locationAnalysis.bicycling') },
        { name: 'walking', icon: 'mdi-walk', text: this.$i18n.t('sidebar.locationAnalysis.walking') },
      ];
    },
    checkboxInput() {
      return {
        dataType: { name: 'checkbox' },
        hideDetails: true,
        disabled: this.loading,
      };
    },

    // Step 4
    analysisNameInput() {
      return {
        dataType: { name: 'text' },
        label: this.$i18n.t('sidebar.locationAnalysis.analysisName'),
        hideDetails: true,
        readonly: this.loading,
      };
    },
    analysisTimeInput() {
      return {
        dataType: { name: 'datetime' },
        label: this.$i18n.t('sidebar.locationAnalysis.analysisTime'),
        hideDetails: true,
        timestamp: true,
        readonly: this.loading,
        datePickerProps: { min: this.$dayjs().toISOString() },
        timePickerProps: {
          ...(this.params.time &&
          this.$dayjs.unix(this.params.time).format('YYYY-MM-DD') === this.$dayjs().format('YYYY-MM-DD')
            ? { min: this.$dayjs().format('HH:mm') }
            : {}),
        },
      };
    },

    // Edit analysys
    editAnalysisInputs() {
      return [
        {
          name: 'name',
          translationPath: 'dialog.body.name',
          rules: [v => !!v || 'rules.required'],
          dataType: {
            name: 'text',
          },
          cols: '12',
        },
      ];
    },
  },
  data: () => ({
    currentEditingAnalysis: undefined,
    changeNameDialogLoaded: false,
    changeNameDialogVisible: false,
    changeNameDialogLoading: false,
    deleteDialogLoaded: false,
    deleteDialogVisible: false,
    deleteDialogLoading: false,
    resultDialogLoaded: false,
    resultDialogVisible: false,
    resultDialogLoading: false,
    fetched: false,
    loading: false,

    params: {
      data_layer_id: null,
      data_name_column: '',
      data_count_column: '',
      start_point: null,
      start_layer_id: null,
      start_feature_id: null,
      walking: false,
      bicycling: false,
      driving: false,
      transit: false,
      time: null,
      name: '',
    },
    saveResult: false,
    currentAnalysis: undefined,
    currentResult: undefined,
    pointType: 'map',
    // if pointType === 'map'
    geometry: null,
    currentEpsgCoordinates: null,
    // if pointType === 'layer'
    layerFeaturesLoading: false,
    currentLayerFeatures: [],
  }),
  methods: {
    getLayerFeatures: call('layers/getLayerFeatures'),
    getAnalyzes: call('locationAnalysis/getLocationAnalyzes'),
    getAnalysis: call('locationAnalysis/getLocationAnalysis'),
    deleteAnalysis: call('locationAnalysis/deleteLocationAnalysis'),
    editAnalysis: call('locationAnalysis/editLocationAnalysis'),
    runLocationAnalysis: call('locationAnalysis/runLocationAnalysis'),

    deleteCoordinates() {
      this.params['start_point'] = null;
    },
    zoomToCoordinates() {
      const point = new Point(this.params['start_point']);
      this.$root.$emit('fitView', point);
    },
    onFeatureMapInput(e) {
      if (!e?.length) return;
      const featureId = e?.[0]?.id || null;
      if (!this.params['start_layer_id']) {
        this.params['start_layer_id'] = e?.[0]?.layerId || null;
        this.onLayerChanged(this.params['start_layer_id'], featureId);
      } else {
        this.params['start_feature_id'] = featureId;
      }
      this.$refs['feature-input']?.$refs['data-input'].toggleOff();
    },
    async init() {
      await this.getAnalyzes();
      this.fetched = true;
    },
    async onLayerChanged(layerId, featureId = null) {
      this.params['start_feature_id'] = null;
      this.currentLayerFeatures = [];
      if (!layerId && layerId !== 0) return;
      this.$refs['feature-input']?.$refs['data-input'].toggleOff();
      this.layerFeaturesLoading = true;
      try {
        const payload = {
          layer_id: layerId,
          params: {
            ids_descs_only: true,
          },
        };
        const r = await this.getLayerFeatures(payload);
        this.currentLayerFeatures = r.data.data?.ids || [];
        if (featureId || featureId === 0) {
          this.params['start_feature_id'] = featureId;
        }
      } finally {
        this.layerFeaturesLoading = false;
      }
    },
    async runAnalysis(params = JSON.parse(JSON.stringify(this.params))) {
      if (params['start_point']) {
        params['start_point'] = transform(params['start_point'], this.$_config.defaultEpsg, 'EPSG:4326');
        params['start_point'] = [params['start_point'][1], params['start_point'][0]];
        delete params['start_feature_id'];
        delete params['start_layer_id'];
      } else {
        delete params['start_point'];
      }
      // Google api does not allow past dates
      if (this.$dayjs().unix() > params.time) params.time = this.$dayjs().unix();
      this.loading = true;
      try {
        const rAnalysisData = await this.runLocationAnalysis(params);

        this.currentResult = {
          data: rAnalysisData,
          transportTypes: this.transportTypes.filter(type => this.params[type.name]).map(type => type.name),
          ...(params['name'] && { name: params['name'] }),
        };
      } finally {
        this.loading = false;
      }
    },
    resetAnalysis() {
      this.currentResult = undefined;
      this.saveResult = false;
      this.params = {
        data_layer_id: null,
        data_name_column: '',
        data_count_column: '',
        start_point: null,
        start_layer_id: null,
        start_feature_id: null,
        walking: false,
        bicycling: false,
        driving: false,
        transit: false,
        time: null,
        name: '',
      };
      this.$refs.stepper?.reset();
    },
    async toggleAnalysisResult(e) {
      const analysis = await this.getAnalysis(e.id);
      const transportTypes = this.transportTypes
        .filter(type => Object.keys(analysis.data?.[0]?.data || {}).find(key => key.includes(type.name)))
        .map(type => type.name);
      this.openAnalysisResultDialog({ ...analysis, transportTypes });
    },
    openAnalysisResultDialog(analysis = this.currentResult) {
      this.resultDialogLoaded = true;
      this.resultDialogVisible = true;
      this.currentAnalysis = analysis;
    },
    onEditAnalysis(e, actionType) {
      this.currentEditingAnalysis = JSON.parse(JSON.stringify(e));
      this[`${actionType}DialogLoaded`] = true;
      this[`${actionType}DialogVisible`] = true;
    },
    async changeAnalysisNameAction(analysis = this.currentEditingAnalysis) {
      this.changeNameDialogLoading = true;
      try {
        await this.editAnalysis({
          id: analysis.id,
          payload: {
            name: analysis.name,
          },
        });
        this.changeNameDialogVisible = false;
      } finally {
        this.changeNameDialogLoading = false;
      }
    },
    async deleteAnalysisAction(id = this.currentEditingAnalysis?.id) {
      this.deleteDialogLoading = true;
      try {
        await this.deleteAnalysis(id);
        this.deleteDialogVisible = false;
      } finally {
        this.deleteDialogLoading = false;
      }
    },
  },
  watch: {
    pointType: {
      handler(nV) {
        if (nV === 'layer') {
          this.$refs['geometry-input']?.$refs['data-input'].toggleOff();
          this.params['start_point'] = null;
        } else if (nV === 'map') {
          this.$refs['feature-input']?.$refs['data-input'].toggleOff();
          this.params['start_layer_id'] = null;
          this.params['start_feature_id'] = null;
          this.currentLayerFeatures = [];
        }
      },
    },
    'params.data_layer_id': {
      handler() {
        this.params['data_name_column'] = '';
        this.params['data_count_column'] = '';
      },
    },
    saveResult(nV) {
      if (!nV) this.params['name'] = '';
    },
    geometry: {
      handler(nV) {
        if (nV) {
          this.$refs['geometry-input']?.$refs['data-input'].toggleOff();
          this.params['start_point'] = nV?.coordinates;
        }
      },
    },
  },
  mounted() {
    this.init();
    this.$watch(
      vm => {
        return { currentEpsg: vm.cursorPositionEpsg, start_point: vm.params['start_point'] };
      },
      nV => {
        const { currentEpsg, start_point } = nV;
        this.currentEpsgCoordinates = start_point
          ? transform(start_point, this.$_config.defaultEpsg, currentEpsg).map(c => c.toFixed(6))
          : null;
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
};
</script>

<style lang="scss" scoped>
.border {
  @include border(all, 1px);
}
::v-deep {
  .v-text-field__details {
    padding: 0 !important;
  }
}
</style>
